import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useEffect, useCallback } from 'react';
import InformationCircleIcon from '@heroicons/react/outline/InformationCircleIcon';
import { TemplateRequestStatus } from '@tribeplatform/gql-client/types';
import { useRouter } from '@tribeplatform/react-sdk';
import { useCollections, useCreateSpace, useCreateSpaceFromTemplate, useTemplateRequest, } from '@tribeplatform/react-sdk/hooks';
import { Alert } from '@tribeplatform/react-ui-kit/Alert';
import { Button } from '@tribeplatform/react-ui-kit/Button';
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons';
import { Modal } from '@tribeplatform/react-ui-kit/Modal';
import { toast } from '@tribeplatform/react-ui-kit/Toast';
import { Form } from '../../Form';
import { T, useI18n } from '../../i18n';
export const CreateSpaceModal = ({ space = {}, templateId, open, onClose, }) => {
    const intl = useI18n();
    const inputRef = useRef();
    const { redirect } = useRouter();
    const { data: collections } = useCollections({
        fields: {
            space: {
                image: 'basic',
            },
        },
        variables: {},
    });
    const { mutateAsync: createSpace, isLoading } = useCreateSpace();
    const [templateRequestId, setTemplateRequestId] = useState(null);
    const [createSpaceMessage, setCreateSpaceMessage] = useState(null);
    const { mutateAsync: createSpaceFromTemplate, isLoading: isLoadingFromTemplate, } = useCreateSpaceFromTemplate();
    const { data: templateRequest } = useTemplateRequest({
        fields: {
            createdEntity: 'basic',
        },
        variables: {
            id: templateRequestId,
        },
        useQueryOptions: {
            enabled: !!templateRequestId,
            refetchInterval: 1000,
            onError: () => {
                toast({
                    title: intl.$t({
                        id: 'Generics.SomethingWentWrong',
                        defaultMessage: 'Something went wrong',
                    }),
                    status: 'error',
                });
                setTemplateRequestId(null);
            },
        },
    });
    const onFulfilled = useCallback(space => {
        onClose();
        // setTimeout(() => {
        //   queryClient.invalidateQueries(getSpacesKey())
        //   queryClient.invalidateQueries(getNetworkKey())
        //   queryClient.invalidateQueries(getAuthMemberKey())
        // }, 1000)
        // router.push(`${space?.relativeUrl}?new=true`)
        setTimeout(() => {
            redirect(`${space === null || space === void 0 ? void 0 : space.relativeUrl}?new=true`);
        }, 500);
    }, [onClose]);
    useEffect(() => {
        if ((templateRequest === null || templateRequest === void 0 ? void 0 : templateRequest.status) === TemplateRequestStatus.Succeeded) {
            setTemplateRequestId(null);
            setCreateSpaceMessage('Done!');
            onFulfilled(templateRequest.createdEntity);
        }
        else if ((templateRequest === null || templateRequest === void 0 ? void 0 : templateRequest.status) === TemplateRequestStatus.Failed) {
            setTemplateRequestId(null);
            onClose();
            toast({
                title: intl.$t({
                    id: 'Space.CreateSpaceModal.CreateFailedTitle',
                    defaultMessage: 'Something went wrong while creating {SPACE}',
                }),
                description: intl.$t({
                    id: 'Generics.TryAgainLater',
                    defaultMessage: 'Please try again later.',
                }),
                status: 'error',
            });
        }
        else if ((templateRequest === null || templateRequest === void 0 ? void 0 : templateRequest.status) === TemplateRequestStatus.InProgress) {
            if (templateRequest === null || templateRequest === void 0 ? void 0 : templateRequest.createdEntityId) {
                setCreateSpaceMessage(intl.$t({
                    id: 'Space.CreateSpaceModal.WrappingUp',
                    defaultMessage: 'Wrapping up...',
                }));
            }
        }
    }, [templateRequest, onFulfilled, onClose, intl]);
    return (_jsxs(Modal, { open: open, onClose: onClose, initialFocus: inputRef, children: [_jsx(Modal.Header, { title: intl.$t({
                    id: 'Space.CreateSpaceModal.HeaderTitle',
                    defaultMessage: 'Create a new space',
                }) }, void 0), _jsx(Modal.Content, { children: _jsx(Form, { defaultValues: space, onSubmit: async (data) => {
                        const { name, collectionId, description, private: _private, hidden, } = data;
                        if (!templateId) {
                            return createSpace({
                                input: {
                                    name,
                                    description,
                                    private: _private,
                                    hidden,
                                    collectionId,
                                },
                            }).then(onFulfilled);
                        }
                        setCreateSpaceMessage(intl.$t({
                            id: 'Space.CreateSpaceModal.CratingSpaceMessage',
                            defaultMessage: 'Creating the {SPACE}...',
                        }));
                        const templateRequest = await createSpaceFromTemplate({
                            input: {
                                name,
                                description,
                                private: _private,
                                hidden,
                                collectionId,
                            },
                            templateId,
                            style: '',
                        });
                        setTemplateRequestId(templateRequest.id);
                    }, children: ({ watch, setValue, setError }) => {
                        var _a;
                        const [isPrivate] = watch(['private']);
                        if (!isPrivate) {
                            setValue('hidden', false);
                        }
                        return (_jsxs("div", { className: "flex flex-col space-y-5", children: [_jsx(Form.Input, { ref: inputRef, label: intl.$t({
                                        id: 'Generics.Name.Noun',
                                        defaultMessage: 'Name',
                                    }), name: "name", validation: {
                                        required: intl.$t({
                                            id: 'Space.CreateSpaceModal.NameFieldRequiredMessage',
                                            defaultMessage: 'Please enter a name for the {SPACE}',
                                        }),
                                    }, placeholder: intl.$t({
                                        id: 'Space.CreateSpaceModal.NameFieldPlaceholder',
                                        defaultMessage: 'e.g. Say Hello',
                                    }) }, void 0), _jsx(Form.Textarea, { label: intl.$t({
                                        id: 'Generics.Description',
                                        defaultMessage: 'Description',
                                    }), name: "description", placeholder: intl.$t({
                                        id: 'Space.CreateSpaceModal.DescriptionPlaceholder',
                                        defaultMessage: 'What is this {SPACE} about? (e.g. Introduce yourself and get to know other {MEMBERS})',
                                    }) }, void 0), _jsx(Form.Toggle, { helperText: intl.$t({
                                        id: 'Space.CreateSpaceModal.MakePrivateToggleHelperText',
                                        defaultMessage: 'Only {MEMBERS} can see who’s in the {SPACE} and what they post.',
                                    }), label: intl.$t({
                                        id: 'Space.CreateSpaceModal.MakePrivateToggleLabel',
                                        defaultMessage: 'Make private',
                                    }), name: "private" }, void 0), isPrivate && (_jsxs(_Fragment, { children: [_jsx(Form.Toggle, { helperText: intl.$t({
                                                id: 'Space.CreateSpaceModal.HideSpaceToggleHelperText',
                                                defaultMessage: 'Hide this {SPACE} from non {SPACE} {MEMBERS}.',
                                            }), label: intl.$t({
                                                id: 'Space.CreateSpaceModal.HideSpaceToggleLabel',
                                                defaultMessage: 'Hide space',
                                            }), name: "hidden" }, void 0), _jsx(Alert, { status: "neutral", icon: _jsx(InformationCircleIcon, {}, void 0), children: _jsx(T, { id: "Space.CreateSpaceModal.HideSpaceAlertText", defaultMessage: "It\u2019s recommended to start your {SPACE} as a hidden one and unhide it when it\u2019s ready." }, void 0) }, void 0)] }, void 0)), _jsx(Form.Select, { label: intl.$t({
                                        id: 'Generics.Collection',
                                        defaultMessage: '{COLLECTION_CC}',
                                    }), name: "collectionId", placeholder: intl.$t({
                                        id: 'Space.CreateSpaceModal.SelectCollectionPlaceholder',
                                        defaultMessage: 'Select a {COLLECTION}...',
                                    }), validation: {
                                        validate: value => {
                                            if (value !== undefined) {
                                                return true;
                                            }
                                            setTimeout(() => {
                                                setError('collectionId', {
                                                    type: 'validate',
                                                    message: intl.$t({
                                                        id: 'Space.CreateSpaceModal.SelectCollectionValidationMessage',
                                                        defaultMessage: 'Please select a {COLLECTION} or pick "No {COLLECTION}".',
                                                    }),
                                                });
                                            }, 10);
                                            return false;
                                        },
                                    }, items: [
                                        {
                                            text: intl.$t({
                                                id: 'Space.CreateSpaceModal.SelectCollectionItemNoCollectionText',
                                                defaultMessage: 'No {COLLECTION}',
                                            }),
                                            value: null,
                                        },
                                        ...(_a = (collections || [])) === null || _a === void 0 ? void 0 : _a.map(collection => ({
                                            text: collection === null || collection === void 0 ? void 0 : collection.name,
                                            value: collection === null || collection === void 0 ? void 0 : collection.id,
                                        })),
                                    ] }, void 0), _jsxs(Form.Actions, { className: "items-center", children: [_jsx(Button, { variant: "primary", type: "submit", disabled: isLoading || isLoadingFromTemplate || templateRequestId, children: _jsx(T, { id: "Generics.Create", defaultMessage: "Create" }, void 0) }, void 0), _jsx(Button, { variant: "outline", onClick: onClose, children: _jsx(T, { id: "Generics.Cancel", defaultMessage: "Cancel" }, void 0) }, void 0), createSpaceMessage ? (_jsxs("div", { className: "flex-1 flex space-s-2 items-center", children: [_jsx(SpinnerIcon, { className: "animate-spin h-4 w-4" }, void 0), ' ', _jsx("span", { children: createSpaceMessage }, void 0)] }, void 0)) : null] }, void 0)] }, void 0));
                    } }, void 0) }, void 0)] }, void 0));
};
//# sourceMappingURL=CreateSpaceModal.js.map